import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Mic } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent } from '../components/ui/Card';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { API_BASE_URL } from '../lib/api';

interface Account {
  id: string;
  accountName: string;
}

interface Deal {
  id: string;
  dealName: string;
}

interface ActivityFormData {
  activityId: string;
  activityTitle: string;
  accountId: string;
  dealId: string;
  activityDatetime: string;
  activityDuration: string;
  activityPurpose: string;
  activityMethod: string;
  activityLocation: string;
  tsAttendees: string;
  clientAttendees: string;
  activityContent: string;
  activityOutcome: string;
  nextActionPlan: string;
}

const initialFormData: ActivityFormData = {
  activityId: '',
  activityTitle: '',
  accountId: '',
  dealId: '',
  activityDatetime: '',
  activityDuration: '',
  activityPurpose: '',
  activityMethod: '',
  activityLocation: '',
  tsAttendees: '',
  clientAttendees: '',
  activityContent: '',
  activityOutcome: '',
  nextActionPlan: '',
};

const methodOptions = ['訪問', 'Web会議', '電話', 'メール', 'その他'];
const purposeOptions = ['ヒアリング', '提案', '見積提示', '契約', 'フォローアップ', 'その他'];

export function ActivityForm() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [formData, setFormData] = useState<ActivityFormData>(initialFormData);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);

  useEffect(() => {
    fetchAccounts();
    if (isEdit) {
      fetchActivity();
    } else {
      const accountId = searchParams.get('accountId');
      const dealId = searchParams.get('dealId');
      const newId = `ACT-${Date.now().toString(36).toUpperCase()}`;
      const now = new Date();
      const datetimeLocal = now.toISOString().slice(0, 16);
      
      setFormData(prev => ({ 
        ...prev, 
        activityId: newId,
        accountId: accountId || '',
        dealId: dealId || '',
        activityDatetime: datetimeLocal,
      }));
      
      if (accountId) {
        fetchDeals(accountId);
      }
      setIsLoading(false);
    }
  }, [id]);

  const fetchAccounts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts?limit=1000`, {
        credentials: 'include',
      });
      const data = await response.json();
      setAccounts(data.data || []);
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  const fetchDeals = async (accountId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/deals?accountId=${accountId}&limit=100`, {
        credentials: 'include',
      });
      const data = await response.json();
      setDeals(data.data || []);
    } catch (err) {
      console.error('Failed to fetch deals:', err);
    }
  };

  const fetchActivity = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/activities/${id}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('商談情報の取得に失敗しました');
      }
      
      const data = await response.json();
      const activity = data.activity;
      
      setFormData({
        activityId: activity.activityId || '',
        activityTitle: activity.activityTitle || '',
        accountId: activity.accountId || '',
        dealId: activity.dealId || '',
        activityDatetime: activity.activityDatetime?.slice(0, 16) || '',
        activityDuration: activity.activityDuration?.toString() || '',
        activityPurpose: activity.activityPurpose || '',
        activityMethod: activity.activityMethod || '',
        activityLocation: activity.activityLocation || '',
        tsAttendees: activity.tsAttendees || '',
        clientAttendees: activity.clientAttendees || '',
        activityContent: activity.activityContent || '',
        activityOutcome: activity.activityOutcome || '',
        nextActionPlan: activity.nextActionPlan || '',
      });

      if (activity.accountId) {
        fetchDeals(activity.accountId);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'accountId' && value) {
      fetchDeals(value);
      setFormData(prev => ({ ...prev, dealId: '' }));
    }
  };

  const handleTranscript = (text: string) => {
    setFormData(prev => ({
      ...prev,
      activityContent: prev.activityContent + (prev.activityContent ? '\n' : '') + text,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      const url = isEdit 
        ? `${API_BASE_URL}/api/activities/${id}`
        : `${API_BASE_URL}/api/activities`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          activityDuration: formData.activityDuration ? parseInt(formData.activityDuration) : null,
          activityDatetime: formData.activityDatetime || null,
          dealId: formData.dealId || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '保存に失敗しました');
      }

      navigate(`/activities/${data.id || id}`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          style={{ color: 'var(--text-secondary)' }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          {isEdit ? '商談を編集' : '商談を新規作成'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="space-y-6 pt-6">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* 基本情報 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="商談ID"
                name="activityId"
                value={formData.activityId}
                onChange={handleChange}
                required
                disabled={isEdit}
              />
              <Input
                label="商談タイトル"
                name="activityTitle"
                value={formData.activityTitle}
                onChange={handleChange}
                placeholder="定期訪問、提案説明など"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  企業 *
                </label>
                <select
                  name="accountId"
                  value={formData.accountId}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">選択してください</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>{account.accountName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  案件
                </label>
                <select
                  name="dealId"
                  value={formData.dealId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">選択してください</option>
                  {deals.map(deal => (
                    <option key={deal.id} value={deal.id}>{deal.dealName}</option>
                  ))}
                </select>
              </div>
            </div>

            <hr style={{ borderColor: 'var(--border-color)' }} />

            {/* 日時・場所 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="日時"
                name="activityDatetime"
                type="datetime-local"
                value={formData.activityDatetime}
                onChange={handleChange}
              />
              <Input
                label="所要時間（分）"
                name="activityDuration"
                type="number"
                value={formData.activityDuration}
                onChange={handleChange}
                placeholder="60"
              />
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  方法
                </label>
                <select
                  name="activityMethod"
                  value={formData.activityMethod}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">選択してください</option>
                  {methodOptions.map(method => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  目的
                </label>
                <select
                  name="activityPurpose"
                  value={formData.activityPurpose}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">選択してください</option>
                  {purposeOptions.map(purpose => (
                    <option key={purpose} value={purpose}>{purpose}</option>
                  ))}
                </select>
              </div>
              <Input
                label="場所"
                name="activityLocation"
                value={formData.activityLocation}
                onChange={handleChange}
                placeholder="オフィス、Web会議など"
              />
            </div>

            <hr style={{ borderColor: 'var(--border-color)' }} />

            {/* 参加者 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="自社参加者"
                name="tsAttendees"
                value={formData.tsAttendees}
                onChange={handleChange}
                placeholder="田中、鈴木"
              />
              <Input
                label="先方参加者"
                name="clientAttendees"
                value={formData.clientAttendees}
                onChange={handleChange}
                placeholder="山田部長、佐藤課長"
              />
            </div>

            <hr style={{ borderColor: 'var(--border-color)' }} />

            {/* 商談内容 */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  商談内容
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  leftIcon={<Mic size={14} />}
                  onClick={() => setShowVoiceRecorder(!showVoiceRecorder)}
                >
                  音声入力
                </Button>
              </div>
              
              {showVoiceRecorder && (
                <div className="mb-4 p-4 border rounded-lg" style={{ borderColor: 'var(--border-color)' }}>
                  <VoiceRecorder onTranscript={handleTranscript} />
                </div>
              )}

              <textarea
                name="activityContent"
                value={formData.activityContent}
                onChange={handleChange}
                rows={8}
                className="w-full px-3 py-2 border rounded-lg resize-none"
                style={{
                  backgroundColor: 'var(--background-card)',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-primary)',
                }}
                placeholder="商談の内容を入力してください..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  商談結果
                </label>
                <textarea
                  name="activityOutcome"
                  value={formData.activityOutcome}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg resize-none"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  次のアクション
                </label>
                <textarea
                  name="nextActionPlan"
                  value={formData.nextActionPlan}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg resize-none"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </div>

            {/* ボタン */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                キャンセル
              </Button>
              <Button
                type="submit"
                leftIcon={<Save size={16} />}
                isLoading={isSaving}
              >
                {isEdit ? '更新' : '作成'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
